import { Stack, Typography, Box, Button, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../store';
import { IChatMessage } from '../../store/reducers/chatbotSlice';
import { colors } from '../theme/theme';

import { MessageBubble } from './MessageBubble';
import { MessageShimmer } from './ShimmerLoader';

interface ChatMessagesProps {
    messages: IChatMessage[];
    isLoading: boolean;
    error: string | null;
    onRetry?: () => void;
    canRetry?: boolean;
    onShowQuickActions?: () => void;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
    messages,
    isLoading,
    error,
    onRetry,
    canRetry = false,
    onShowQuickActions,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const endOfMessagesRef = useRef<HTMLDivElement>(null);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const brandPrimaryColor = brandDetails?.PrimaryColour || colors.bluePreset[10];

    const scrollToBottom = () => {
        endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        const updateScrollButtonVisibility = () => {
            const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            const hasOverflow = container.scrollHeight > container.clientHeight + 8;
            setShowScrollToBottom(hasOverflow && distanceFromBottom > 100);
        };

        updateScrollButtonVisibility();
        container.addEventListener('scroll', updateScrollButtonVisibility);

        return () => {
            container.removeEventListener('scroll', updateScrollButtonVisibility);
        };
    }, [messages, isLoading]);

    return (
        <Stack
            ref={scrollContainerRef}
            sx={{
                flex: 1,
                overflow: 'auto',
                backgroundColor: alpha(brandPrimaryColor, 0.04),
                px: 1,
                py: 2,
                gap: 0.8,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {messages.length === 0 ? (
                <Stack
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ color: colors.greyPreset[50], textAlign: 'center' }}>
                        No messages yet. Start a conversation!
                    </Typography>
                </Stack>
            ) : (
                <>
                    {messages.map(message => (
                        <MessageBubble key={message.id} message={message} onShowQuickActions={onShowQuickActions} />
                    ))}
                    {isLoading && (
                        <Stack alignItems="flex-start" gap={1} sx={{ mb: 1, mt: 1 }}>
                            <Stack direction="row" gap={1} alignItems="flex-start" sx={{ width: '70%' }}>
                                <span
                                    className="icon-rounded-chat"
                                    style={{ fontSize: 20, color: brandPrimaryColor, flexShrink: 0, marginTop: 4 }}
                                />
                                <Stack gap={1} sx={{ width: '100%' }}>
                                    <MessageShimmer />
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                    {error && (
                        <Box
                            sx={{
                                backgroundColor: '#ffebee',
                                borderRadius: '12px',
                                p: 2,
                                borderLeft: `4px solid #c62828`,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#c62828' }}>
                                {error}
                            </Typography>
                            {canRetry && onRetry && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={onRetry}
                                    sx={{
                                        mt: 1.25,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: brandPrimaryColor,
                                        '&:hover': {
                                            backgroundColor: brandPrimaryColor,
                                            filter: 'brightness(0.92)',
                                        },
                                    }}
                                >
                                    Retry
                                </Button>
                            )}
                        </Box>
                    )}
                    <div ref={endOfMessagesRef} />
                </>
            )}

            {showScrollToBottom && (
                <IconButton
                    aria-label="Scroll to bottom"
                    onClick={scrollToBottom}
                    sx={{
                        position: 'sticky',
                        bottom: 8,
                        ml: 'auto',
                        width: 32,
                        height: 32,
                        backgroundColor: brandPrimaryColor,
                        color: colors.fullWhite,
                        border: `1px solid ${alpha(brandPrimaryColor, 0.3)}`,
                        boxShadow: `0 8px 16px ${alpha(brandPrimaryColor, 0.35)}`,
                        '&:hover': {
                            backgroundColor: brandPrimaryColor,
                            filter: 'brightness(0.92)',
                        },
                    }}
                >
                    <span className="icon-arrow-down" style={{ fontSize: 14 }} />
                </IconButton>
            )}
        </Stack>
    );
};
